import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { redirectErrorPage } from '../../utils/errors';
import { Message, Dropdown } from '../../components/PrimereactComponents';
import { useTranslation } from 'react-i18next';
import LoadingPage from '../../components/LoadingPage';
import { URLKeys } from '../../Constants';
import Intro from '../../components/common/Intro/Intro';
import Section from '../../components/common/Section/Section';
import ComparisonDetail from '../../components/membersComparison/ComparisonDetail';
import './MembersComparison.css';
import { DropdownOptionType } from '../../types';

const MembersComparison = () => {
    const [loading, setLoading] = useState(true);
    const searchParams = new URLSearchParams(window.location.search);
    let firstMemberValue = searchParams.get(URLKeys.firstMember) || null;
    let secondMemberValue = searchParams.get(URLKeys.secondMember) || null;
    interface Member {
        code: number;
        name: string;
    }

    const [firstMember, setFirstMember] = useState<Member | null>(null);
    const [secondMember, setSecondMember] = useState<Member | null>(null);

    const { municipalitaId, bodyType, zastupitelstvoId } = useParams();
    const { t } = useTranslation(['analyses', 'translation']);

    const [memberOptions, setMemberOptions] = useState<Member[] | undefined>(undefined);

    useEffect(() => {
        const fetchData = async () => {
            try {
                await axios
                    .get(`FE/${municipalitaId}/${bodyType}/${zastupitelstvoId}/dropdownMemberOptions`)
                    .then((response) => {
                        setMemberOptions(response.data);
                        setFirstMember(
                            firstMemberValue !== null && response.data
                                ? response.data.find(
                                      (member: DropdownOptionType) => member.code === parseInt(firstMemberValue)
                                  )
                                : null
                        );

                        setSecondMember(
                            secondMemberValue !== null && response.data
                                ? response.data.find(
                                      (member: DropdownOptionType) => member.code === parseInt(secondMemberValue)
                                  )
                                : null
                        );
                    });
            } catch (error) {
                redirectErrorPage(error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [bodyType, firstMemberValue, municipalitaId, secondMemberValue, zastupitelstvoId]);

    if (loading) {
        return <LoadingPage />;
    }

    const setParams = (key: string, value: string | number): void => {
        const url = new URL(window.location.toString());
        url.searchParams.set(key, value.toString());
        window.history.replaceState({}, '', url);
    };
    const clearParams = (key: string): void => {
        const url = new URL(window.location.toString());
        url.searchParams.delete(key);
        window.history.replaceState({}, '', url);
    };

    const onFirstMemberChange = (e: { value: DropdownOptionType }) => {
        setFirstMember(e.value);
        if (e.value !== undefined) {
            setParams(URLKeys.firstMember, e?.value.code);
        } else {
            setFirstMember(null);
            clearParams(URLKeys.firstMember);
        }
    };

    const onSecondMemberChange = (e: { value: DropdownOptionType }) => {
        setSecondMember(e.value);
        if (e.value !== undefined) {
            setParams(URLKeys.secondMember, e.value.code);
        } else {
            setSecondMember(null);
            clearParams(URLKeys.secondMember);
        }
    };

    if (memberOptions === undefined) {
        return <LoadingPage />;
    }

    return (
        <>
            <Intro title={t('councilMember_comparison', { ns: 'translation' })} />
            <Section contentClassName='members-comparison-analyses-selection-wrapper'>
                <span className='p-float-label'>
                    <Dropdown
                        value={firstMember}
                        options={memberOptions.filter((option) => option.code !== secondMember?.code)}
                        onChange={onFirstMemberChange}
                        optionLabel='name'
                        filter
                        showClear
                        filterBy='name'
                        inputId='firstMemberSelection'
                    />
                    <label htmlFor='firstMemberSelection'>{t('select-first-member')}</label>
                </span>
                <span className='p-float-label'>
                    <Dropdown
                        value={secondMember}
                        options={memberOptions.filter((option) => option.code !== firstMember?.code)}
                        onChange={onSecondMemberChange}
                        optionLabel='name'
                        filter
                        showClear
                        filterBy='name'
                        inputId='secondMemberSelection'
                    />
                    <label htmlFor='secondMemberSelection'>{t('select-second-member')}</label>
                </span>
            </Section>
            {firstMember === null || secondMember === null ? (
                <Message severity='info' text={t('select-two-different')} style={{ margin: '0 auto' }} />
            ) : (
                <ComparisonDetail firstMemberId={firstMember.code} secondMemberId={secondMember.code} />
            )}
        </>
    );
};

export default MembersComparison;
